/**
 * Per-type comparators for the admin table sort mechanism. Pure, standalone
 * functions with no React and no knowledge of any particular page — every
 * sortable table in the admin composes these instead of five hand-rolled
 * `.sort()` calls that would slowly drift from each other. See
 * `useTableSort.ts` for the hook that wires these into a table, and
 * docs/decisions/2026-07-31-admin-table-sort-filter.md for the reasoning.
 */

export type ColumnValueType = 'string' | 'number' | 'date' | 'boolean';
export type SortDirection = 'asc' | 'desc';

/**
 * Locale-aware, case-insensitive, and numeric-aware: "SKU-2" sorts before
 * "SKU-10" instead of after it, which a plain `<` comparison would get wrong.
 */
export function compareStrings(a: string, b: string): number {
  return a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true });
}

export function applyDirection(comparison: number, direction: SortDirection): number {
  return direction === 'asc' ? comparison : -comparison;
}

/**
 * `null`/`undefined`/`NaN` always sort to the end, in *either* direction — a
 * missing value is not "the smallest number", it is simply unknown, so
 * reversing the direction must flip the order of the real values without
 * dragging a missing one to the front. That is why `direction` is threaded
 * through here directly instead of the caller wrapping the whole result in
 * `applyDirection`: negating the "missing sorts last" branches along with
 * the real comparison would turn them into "missing sorts first" on a
 * descending sort, which is wrong.
 */
export function compareNullableNumbers(
  a: number | null | undefined,
  b: number | null | undefined,
  direction: SortDirection = 'asc',
): number {
  const aMissing = a == null || Number.isNaN(a);
  const bMissing = b == null || Number.isNaN(b);
  if (aMissing && bMissing) return 0;
  if (aMissing) return 1;
  if (bMissing) return -1;
  return applyDirection((a as number) - (b as number), direction);
}

/** Same null-last rule as numbers, comparing by timestamp so a bad/blank date never crashes `Date` parsing into `NaN` silently swapping order. */
export function compareNullableDates(
  a: string | null | undefined,
  b: string | null | undefined,
  direction: SortDirection = 'asc',
): number {
  const aTime = a ? new Date(a).getTime() : null;
  const bTime = b ? new Date(b).getTime() : null;
  return compareNullableNumbers(
    aTime == null || Number.isNaN(aTime) ? null : aTime,
    bTime == null || Number.isNaN(bTime) ? null : bTime,
    direction,
  );
}

/** `true` sorts before `false` ascending — scanning "who's still active" reads naturally with active rows on top. */
export function compareBooleans(a: boolean, b: boolean): number {
  return Number(b) - Number(a);
}

/**
 * Dispatches to the right comparator for a column's declared type, already
 * applying `direction` — used by `useTableSort` so a page only ever declares
 * `type`, never picks a comparator or handles direction/null-placement by
 * hand. String and boolean columns have no "missing value" concept in this
 * app's data (a name or an active flag is never null), so `direction` is
 * applied to their whole comparison; numbers and dates route through their
 * own null-aware comparators instead, see the note on `compareNullableNumbers`.
 */
export function compareByType(
  type: ColumnValueType,
  a: unknown,
  b: unknown,
  direction: SortDirection = 'asc',
): number {
  switch (type) {
    case 'string':
      return applyDirection(compareStrings(String(a ?? ''), String(b ?? '')), direction);
    case 'number':
      return compareNullableNumbers(a as number | null | undefined, b as number | null | undefined, direction);
    case 'date':
      return compareNullableDates(a as string | null | undefined, b as string | null | undefined, direction);
    case 'boolean':
      return applyDirection(compareBooleans(Boolean(a), Boolean(b)), direction);
    /* istanbul ignore next -- ColumnValueType is exhaustive; kept for a future type added without updating this switch */
    default:
      return 0;
  }
}
